import { sessionSelector } from 'app/store/session/selectors';
import { initTagManager } from 'app/tag-manager';
import { lazyRetry } from 'app/utils';
import PlayersPage from 'modules/users/pages/PlayersPage';
import React, { Suspense, useEffect, createElement } from 'react';
import { useSelector } from 'react-redux';
import {
  BrowserRouter,
  Navigate,
  Route as ReactRoute,
  Routes as ReactRoutes,
} from 'react-router-dom';

import Loading from 'shared/components/Loading';

import { useKeycloak } from '@react-keycloak/web';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { StoreName } from 'app/enums/store';
import { AppLayout } from 'app/components/Layouts/AppLayout';
import { Route } from 'app/enums';
import { ROUTES } from 'app/routing/routes';
import { GuardedRoute } from 'app/components/GuardedRoute';
import NotFound from 'app/components/Layouts/AppLayout/NotFound';
import { SessionStoreStatus } from 'app/store/session/enums';
import { Loader } from 'shared/components/Loader';
import HomePage from 'modules/home/pages/HomePage';
import { SlimLayout } from 'app/components/Layouts/SlimLayout';
import { CONNECTION_CALLBACK_ROUTES } from 'app/routing/connection-callback-routes';
import { GAME_SESSIONS_ROUTES } from 'modules/game-sessions/routes';

const BalancePage = lazyRetry(() => import('modules/payments/pages/BalancePage'));
const TransactionsPage = lazyRetry(() => import('modules/payments/pages/TransactionsPage'));

const ROUTES_WITH_SLIM_LAYOUT = [...CONNECTION_CALLBACK_ROUTES, ...GAME_SESSIONS_ROUTES];

const Routes = () => {
  const loading = useEntityRepositoryStatus({ [StoreName.Session]: SessionStoreStatus.Loading });
  const activeUser = useSelector(sessionSelector.activeUser);

  const { keycloak } = useKeycloak();

  useEffect(() => {
    initTagManager(activeUser?.id, keycloak.authenticated);
  }, [activeUser?.id, keycloak.authenticated]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <BrowserRouter>
        <ReactRoutes>
          <ReactRoute element={<SlimLayout />}>
            {ROUTES_WITH_SLIM_LAYOUT.map((route) => (
              <ReactRoute
                path={route.path}
                key={`${route.path}${route.component.name}`}
                element={
                  <GuardedRoute
                    key={`${route.path}${route.component.name}`}
                    path={route.path}
                    guards={route.guards}
                  >
                    {createElement(route.component)}
                  </GuardedRoute>
                }
              />
            ))}
          </ReactRoute>
          <ReactRoute element={<AppLayout />}>
            {ROUTES.map((route) => (
              <ReactRoute
                path={route.path}
                key={`${route.path}${route.component.name}`}
                element={
                  <GuardedRoute
                    key={`${route.path}${route.component.name}`}
                    path={route.path}
                    guards={route.guards}
                  >
                    {createElement(route.component)}
                  </GuardedRoute>
                }
              />
            ))}
            <ReactRoute path={Route.PaymentBalance} element={<BalancePage />} />
            <ReactRoute path={Route.PaymentTransactions} element={<TransactionsPage />} />
            <ReactRoute path={Route.Players} element={<PlayersPage />}>
              <ReactRoute path="online" element={<Navigate to={Route.PlayersOnline} />} />
              <ReactRoute
                path="recently-played"
                element={<Navigate to={Route.PlayersRecentlyPlayed} />}
              />
            </ReactRoute>
            <ReactRoute path={Route.NotFound} element={<NotFound />} />
            <ReactRoute path="/match-history" element={<Navigate to={Route.MyActiveMatches} />} />
            <ReactRoute path="/home" element={<Navigate to={Route.Root} />} />
            <ReactRoute path="*" element={<HomePage />} />
          </ReactRoute>
        </ReactRoutes>
      </BrowserRouter>
    </Suspense>
  );
};

export default Routes;
